import { useEffect, useState } from 'react'
import { Download } from 'lucide-react'
import { AdminLayout, PageHeader } from '@/components/layout/AdminLayout'
import { LeadTable } from '@/components/admin/LeadTable'
import { Spinner } from '@/components/ui/Spinner'
import { fetchLeads, getLeadsExportUrl } from '@/lib/api'
import type { Lead } from '@/types'
import { LEAD_STATUSES } from '@/types'

export default function AdminLeads() {
  const [leads, setLeads] = useState<Lead[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage] = useState(1)
  const [pages, setPages] = useState(1)

  useEffect(() => {
    setLoading(true)
    fetchLeads({ status: statusFilter || undefined, page })
      .then((data) => {
        setLeads(data.leads)
        setTotal(data.total)
        setPages(data.pages)
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }, [statusFilter, page])

  return (
    <AdminLayout>
      <PageHeader
        title="Leads"
        subtitle={`${total} total submission${total !== 1 ? 's' : ''}`}
        action={
          <a
            href={getLeadsExportUrl(statusFilter || undefined)}
            download
            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors"
          >
            <Download className="h-4 w-4" /> Export CSV
          </a>
        }
      />

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-5">
        <button
          onClick={() => { setStatusFilter(''); setPage(1) }}
          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
            !statusFilter ? 'bg-brand-500 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
          }`}
        >
          All
        </button>
        {LEAD_STATUSES.map(({ value, label }) => (
          <button
            key={value}
            onClick={() => { setStatusFilter(value); setPage(1) }}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              statusFilter === value
                ? 'bg-brand-500 text-white'
                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading && <div className="flex justify-center py-16"><Spinner size="lg" /></div>}
      {error && <div className="text-red-600 text-sm py-4">{error}</div>}

      {!loading && !error && (
        <>
          <LeadTable leads={leads} />

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 rounded-md border border-slate-200 text-sm disabled:opacity-40 hover:bg-slate-50"
              >
                ← Prev
              </button>
              <span className="text-sm text-slate-500">
                Page {page} of {pages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                disabled={page === pages}
                className="px-3 py-1.5 rounded-md border border-slate-200 text-sm disabled:opacity-40 hover:bg-slate-50"
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  )
}
